import { BoxComponents } from "@boxes/BoxComponents";
import { BoxTypes } from "@boxes/BoxTypes";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactFlow, {
  Background,
  Panel,
  ReactFlowProvider,
  addEdge,
  updateEdge,
  useEdgesState,
  useNodesState,
} from "reactflow";

import * as uuid from "uuid";

import CustomEdge from "@containers/flow/Edge";
import FlowPanel from "./FlowPanel";
import { NodeDrawer } from "./NodeDrawer";

import { BoxTitles } from "@boxes/BoxTitles";
import { FlowBox } from "@boxes/FlowBox";
import style from "@styles/pages/flow/Flow.module.scss";
import "reactflow/dist/base.css";
import { CreateAudienceDrawer } from "./CreateAudienceDrawer";

const edgeTypes = {
  step: CustomEdge,
};

export const Flow = ({ flow, steps, stepId, onChange, locked }) => {
  const reactFlowWrapper = useRef(null);
  const panelRef = useRef(null);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [openNodeDrawer, setOpenNodeDrawer] = useState(false);
  const [selectedToEditNode, setSelectedToEditNode] = useState(null);
  const [panelHeight, setPanelHeight] = useState(0);
  const [flowHeight, setFlowHeight] = useState(0);
  const [openContextMenu, setOpenContextMenu] = useState(false);
  const [openAudienceDrawer, setOpenAudienceDrawer] = useState(false);
  const [audience, setAudience] = useState({});

  useEffect(() => {
    onChange && onChange({ boxes: nodes, links: edges });
    setOpenContextMenu(false);
  }, [nodes, edges]);

  const deleteNode = (e) => {
    if (selectedToEditNode) return;
    if (selectedNode && selectedNode.type !== "Trigger") {
      if (!selectedNode.data || selectedNode.data.type != "edge") {
        setNodes(nodes.filter((n) => selectedNode.id != n.id));
        setEdges(
          edges.filter(
            (e) => selectedNode.id != e.target && selectedNode.id != e.source
          )
        );
      } else {
        setEdges(edges.filter((e) => selectedNode.id != e.id));
      }
      setSelectedNode(null);
    }
  };

  useEffect(() => {
    setNodes((previousValue) => {
      return (steps?.boxes || []).map((step) => {
        step.data.flow_id = flow.id;
        step.data.stepId = stepId;
        return step;
      });
    });

    setEdges(steps?.links || []);
  }, [steps, reactFlowInstance]);

  const onConnect = (params) => {
    const isValid = FlowBox.validateConnection(nodes, params);
    if (isValid) {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: "step",
            label:
              params.sourceHandle == "has"
                ? "Sim"
                : params.sourceHandle == "hasNot"
                ? "Não"
                : null,
            data: { type: "edge" },
          },
          eds
        )
      );
    }
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = (event) => {
    event.preventDefault();

    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const type = event.dataTransfer.getData("application/reactflow");

    // check if the dropped element is valid
    if (typeof type === "undefined" || !type) {
      return;
    }

    const position = reactFlowInstance.project({
      x: event.clientX - reactFlowBounds.left,
      y: event.clientY - reactFlowBounds.top,
    });

    for (const n of nodes) {
      if (
        FlowBox.isInsideBox(position, n) &&
        FlowBox.validateGroupDrop(type, n.type)
      ) {
        const new_node = { data: { type: n.type } };
        switch (n.type) {
          case BoxTypes.AudienceGroup:
            new_node.data.audiences = [
              ...(n.data.audiences || []),
              {
                id: uuid.v4(),
                label: `${BoxTitles[type]}`,
                type,
                data: { type },
              },
            ];
            break;
          case BoxTypes.ConditionalGroup:
            new_node.data.conditionals = [
              ...(n.data.conditionals || []),
              {
                id: uuid.v4(),
                label: `${BoxTitles[type]}`,
                type,
                data: { type },
              },
            ];
            break;
          case BoxTypes.FilterGroup:
            new_node.data.filters = [
              ...(n.data.filters || []),
              {
                id: uuid.v4(),
                label: `${BoxTitles[type]}`,
                type,
                data: { type },
              },
            ];
            break;
          case BoxTypes.CreativeGroupAction:
            new_node.data.creatives = [
              ...(n.data.creatives || []),
              {
                id: uuid.v4(),
                label: `${BoxTitles[type]}`,
                type,
                data: { type },
              },
            ];
            break;
        }
        setNodes(
          nodes.map((nodeData) => {
            if (n.id == nodeData.id) {
              nodeData.data = new_node.data;
            }
            return nodeData;
          })
        );
        return;
      }
    }

    const newNode = {
      id: uuid.v4(),
      type,
      position,
      data: {
        type,
        label: `${BoxTitles[type]}`,
      },
    };

    setNodes((nds) => nds.concat(newNode));
  };

  const onEdgeUpdate = useCallback(
    (oldEdge, newConnection) =>
      setEdges((els) => updateEdge(oldEdge, newConnection, els)),
    []
  );

  const prepareGroupToEdit = (event, node) => {
    switch (node.type) {
      case BoxTypes.AudienceGroup:
        for (const a of node.data?.audiences) {
          if (a.id == event.detail.audienceId) {
            a.selected = true;
          } else {
            a.selected = false;
          }
        }
        break;
      case BoxTypes.FilterGroup:
        for (const f of node.data?.filters) {
          if (f.id == event.detail.filterId) {
            f.selected = true;
          } else {
            f.selected = false;
          }
        }
        break;
      case BoxTypes.ConditionalGroup:
        for (const c of node.data?.conditionals) {
          if (c.id == event.detail.conditionalId) {
            c.selected = true;
          } else {
            c.selected = false;
          }
        }
        break;
      case BoxTypes.CreativeGroupAction:
        for (const c of node.data?.creatives) {
          if (c.id == event.detail.creativeId) {
            c.selected = true;
          } else {
            c.selected = false;
          }
        }
        break;
      default:
        return null;
    }

    return node;
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      setFlowHeight(reactFlowWrapper.current.getBoundingClientRect().height);
    }
  }, []);

  return (
    <ReactFlowProvider>
      <div
        className={style.flowWrapper}
        ref={reactFlowWrapper}
        style={{ maxHeight: `calc(100vh - ${panelHeight}px` }}
      >
        {flowHeight && (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={BoxComponents}
            edgeTypes={edgeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onEdgeUpdate={onEdgeUpdate}
            snapGrid={[3, 3]}
            snapToGrid
            elementsSelectable={true}
            onNodeClick={(e, node) => {
              if (e.detail.audience) {
                const [b, l] = FlowBox.findPathToRoot(
                  e.detail.boxId,
                  nodes,
                  edges
                );
                setAudience({ boxes: b, links: l });
                setOpenAudienceDrawer(true);
              } else if (!FlowBox.isAGroup(node.type)) {
                setSelectedNode(node);
              } else if (e.detail.setup) {
                const preparedNode = prepareGroupToEdit(e, node);
                setSelectedToEditNode(preparedNode);
                setOpenNodeDrawer(!!preparedNode);
              }
            }}
            // onPaneMouseMove={() => setOpenContextMenu(false)}
            onPaneContextMenu={(e) => {
              e.preventDefault();
              setOpenContextMenu(false);
            }}
            onPaneClick={(e) => setOpenContextMenu(false)}
            onNodeContextMenu={(e, node) => {
              e.preventDefault();

              if (node && node.type !== "Trigger") {
                const { pageX, pageY } = e;

                setOpenContextMenu({
                  show: true,
                  x: pageX,
                  y: pageY,
                  node: node,
                });
              }
            }}
            onNodesDelete={(e) => deleteNode(e)}
            deleteKeyCode={!selectedToEditNode ? ["Backspace", "Delete"] : null}
            defaultViewport={{
              x: 50,
              y: (flowHeight - panelHeight) / 2,
              zoom: 0.75,
            }}
            onNodeDoubleClick={(e, node) => {
              if (node.data?.type == "Trigger") {
                return null;
              }
              if (!FlowBox.isAGroup(node.type)) {
                setOpenNodeDrawer(true);
                setSelectedToEditNode(node);
              }
            }}
            onEdgeClick={(e, edge) => setSelectedNode(edge)}
            defaultEdgeOptions={{
              type: "step",
            }}
          >
            <Background
              gap={50}
              style={{ backgroundColor: "#1A1A1A" }}
              color="#4b4b4b"
            />
            <svg>
              <defs>
                <linearGradient id="edge-gradient">
                  <stop offset="0%" stopColor="#2e2e2e" />
                </linearGradient>
              </defs>
            </svg>
          </ReactFlow>
        )}

        {openContextMenu && (
          <div
            className={style.trash}
            style={{
              top: openContextMenu.y,
              left: openContextMenu.x,
            }}
            onClick={() => {
              setNodes(nodes.filter((n) => openContextMenu.node.id != n.id));
              setOpenContextMenu(false);
            }}
          >
            <div
              className={style.icon}
              data-tooltip={`Excluir "${openContextMenu.node?.data?.label}"`}
            >
              <i className="far fa-trash" />
            </div>
          </div>
        )}

        <Panel position="bottom" style={{ position: "fixed" }} id="flowPanel">
          <FlowPanel
            ref={panelRef}
            onCollapse={(ref) => setPanelHeight(ref.offsetHeight + 110)}
            boxes={nodes}
            restrictions={flow?.restrictions}
          />
        </Panel>
      </div>

      <NodeDrawer
        visible={openNodeDrawer}
        flowType={FlowBox.isEventFlow(nodes) ? "EVENT" : "COMMON"}
        boxes={nodes}
        restrictions={flow?.restrictions}
        node={selectedToEditNode}
        onClose={() => {
          setOpenNodeDrawer(false);
          setSelectedToEditNode(null);
        }}
        onSave={(nodeData, close = true) => {
          setNodes(
            nodes.map((n) => {
              if (n.id == nodeData.id) {
                n.data = nodeData.data;
              }
              return n;
            })
          );
          setSelectedToEditNode(null);

          if (close) setOpenNodeDrawer(false);
        }}
      />
      <CreateAudienceDrawer
        visible={openAudienceDrawer}
        audience={audience}
        onClose={() => {
          setOpenAudienceDrawer(false);
          setAudience(null);
        }}
      />
    </ReactFlowProvider>
  );
};
